import { supabase } from "@/integrations/supabase/client";
import { isMarketingPath } from "@/lib/marketingTracking";

export type CtaType =
  | "get_started"
  | "book_demo"
  | "signin"
  | "signup_started"
  | "newsletter_submit"
  | "contact_submit"
  | "demo_submit"
  | "demo_confirmed"
  | "blog_post_open"
  | "pricing_toggle"
  | "faq_open"
  | "nav_link"
  | "social_click";

const SESSION_KEY = "sorvaine_cta_session_id";

/**
 * Stable per-browser session id (lives in localStorage so it survives reloads
 * and lets us correlate an anonymous click → a later signup).
 */
function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        crypto?.randomUUID?.() ??
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

/**
 * Fire-and-forget CTA click tracking. Never blocks navigation, never throws
 * into the UI — failures are silently swallowed.
 */
export async function trackCtaClick(cta: CtaType, location: string): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    if (!isMarketingPath(window.location.pathname)) return;

    const session_id = getSessionId();
    const { data: userData } = await supabase.auth.getUser();
    const user_id = userData?.user?.id ?? null;

    await supabase.from("cta_events").insert({
      cta_type: cta,
      location: location.slice(0, 100),
      session_id,
      user_id,
      page_path: window.location.pathname.slice(0, 500),
      referrer: document.referrer ? document.referrer.slice(0, 1000) : null,
      user_agent: navigator.userAgent ? navigator.userAgent.slice(0, 1000) : null,
    });
  } catch {
    // analytics must never break UX
  }
}
